import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingCart, Plus, X } from "lucide-react";
import { usePurchaseOrders, useCreatePO, useCreatePOLine } from "@/hooks/usePurchaseOrders";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { VendorSearchInput } from "@/components/shared/VendorSearchInput";
import { useToast } from "@/hooks/use-toast";
import type { Part } from "@/hooks/useParts";

interface OrderPartDialogProps {
  part: Part;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function OrderPartDialog({ part, open, onOpenChange }: OrderPartDialogProps) {
  const { data: draftPOs = [] } = usePurchaseOrders({ status: "draft" });
  const { data: vendors = [] } = useVendorContacts();
  const createPO = useCreatePO();
  const createLine = useCreatePOLine();
  const { toast } = useToast();

  const suggestedQty = Math.max(1, (part.min_quantity ?? 1) - part.qty_in_stock);
  const [quantity, setQuantity] = useState("1");
  const [poSelection, setPoSelection] = useState<string>("new");
  const [vendorId, setVendorId] = useState("");
  const [isPending, setIsPending] = useState(false);

  const eligiblePOs = useMemo(
    () => part.vendor_id ? draftPOs.filter((po) => po.vendor_id === part.vendor_id) : draftPOs,
    [draftPOs, part.vendor_id]
  );

  // Reset state each time dialog opens
  useEffect(() => {
    if (open) {
      setQuantity(suggestedQty.toString());
      setVendorId(part.vendor_id ?? "");

      if (part.vendor_id) {
        // Prefer a draft PO from the same vendor; otherwise start a new one
        const matchingPO = eligiblePOs[0];
        setPoSelection(matchingPO ? matchingPO.id : "new");
      } else {
        // No primary vendor — prefer existing drafts if any, else new PO
        setPoSelection(draftPOs[0]?.id ?? "new");
      }
    }
  }, [open, eligiblePOs, draftPOs, part.vendor_id]);

  const selectedVendor = vendors.find((v) => v.id === vendorId);

  const isNewPO = poSelection === "new";

  const handleSubmit = async () => {
    if (!poSelection || !quantity) return;
    setIsPending(true);
    try {
      // Prevent adding to a PO with a different vendor than the part’s vendor
      if (!isNewPO && part.vendor_id) {
        const selectedPO = draftPOs.find((p) => p.id === poSelection);
        if (selectedPO && selectedPO.vendor_id !== part.vendor_id) {
          throw new Error("Selected purchase order vendor does not match the part vendor.");
        }
      }

      let targetPoId = poSelection;
      let targetPoNumber = "";

      if (isNewPO) {
        const newPO = await createPO.mutateAsync({ vendor_id: vendorId || undefined });
        targetPoId = newPO.id;
        targetPoNumber = newPO.po_number;
      } else {
        targetPoNumber = draftPOs.find((p) => p.id === poSelection)?.po_number ?? poSelection;
      }

      await createLine.mutateAsync({
        po_id: targetPoId,
        part_id: part.id,
        description: part.name,
        quantity: parseFloat(quantity) || 1,
        unit_cost: part.unit_cost ?? 0,
      });

      toast({ title: `Added to ${targetPoNumber}`, description: `${quantity} × ${part.name}` });
      onOpenChange(false);
    } catch {
      // errors surfaced by individual mutations
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Add to Purchase Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <p className="text-sm font-medium">{part.name}</p>
            {part.part_number && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">OEM Part #: {part.part_number}</p>
            )}
          </div>

          {/* Quantity */}
          <div className="flex items-end gap-3">
            <div className="space-y-1.5 w-28">
              <Label className="text-xs">Quantity</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="text-sm text-muted-foreground pb-1.5">
              × {part.unit_cost != null ? `$${part.unit_cost.toFixed(2)}` : "no cost set"}
              {quantity && part.unit_cost != null && (
                <span className="ml-2 font-medium text-foreground">
                  = ${(parseFloat(quantity) * part.unit_cost).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* PO selector */}
          <div className="space-y-1.5">
            <Label className="text-xs">Purchase Order</Label>
            <select
              value={poSelection}
              onChange={(e) => setPoSelection(e.target.value)}
              className="w-full text-sm bg-background border rounded-md px-3 py-1.5 h-8"
            >
              {eligiblePOs.length > 0 ? eligiblePOs.map((po) => {
                const v = vendors.find((v) => v.id === po.vendor_id);
                return (
                  <option key={po.id} value={po.id}>
                    {po.po_number}{v ? ` — ${v.company}` : ""}
                  </option>
                );
              }) : (
                <option key="none" value="new" disabled>
                  {part.vendor_id ? "No draft PO for selected vendor" : "No draft PO available"}
                </option>
              )}
              <option value="new">+ New Purchase Order</option>
            </select>
          </div>

          {/* Vendor picker — only for new POs */}
          {isNewPO && (
            <div className="space-y-1.5">
              <Label className="text-xs">Vendor</Label>
              {selectedVendor ? (
                <div className="flex items-center gap-2 h-8 px-3 rounded-md border bg-primary/5 border-primary/30 text-sm">
                  <span className="flex-1 font-medium text-primary truncate">{selectedVendor.company}</span>
                  <button onClick={() => setVendorId("")} className="text-primary/60 hover:text-primary">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <VendorSearchInput vendors={vendors} onSelect={(v) => setVendorId(v.id)} placeholder="Search vendors…" className="h-8 text-sm" />
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isPending || !quantity}
            className="gap-1.5"
          >
            {isNewPO ? <Plus className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
            {isPending ? "Adding…" : isNewPO ? "Create PO & Add" : "Add to PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
